const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api'

export interface UserGroup {
  id: string
  name: string
  description?: string
  department?: string
  createdBy: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  creator: {
    id: string
    name: string
    email: string
  }
  members: UserGroupMember[]
  enrollments?: GroupEnrollment[]
  _count?: {
    members: number
    enrollments: number
  }
}

export interface UserGroupMember {
  id: string
  userId: string
  userGroupId: string
  role: 'ADMIN' | 'MEMBER'
  joinedAt: string
  createdAt: string
  user: {
    id: string
    name: string
    email: string
    department?: string
    position?: string
  }
}

export interface GroupEnrollment {
  id: string
  userGroupId: string
  testId: string
  dueDate?: string
  assignedAt: string
  test: {
    id: string
    title: string
    description?: string
  }
}

export interface CreateUserGroupRequest {
  name: string
  description?: string
  department?: string
}

export interface UpdateUserGroupRequest {
  name: string
  description?: string
  department?: string
}

export interface AddMemberRequest {
  userId: string
  role?: 'ADMIN' | 'MEMBER'
}

export interface UpdateMemberRoleRequest {
  role: 'ADMIN' | 'MEMBER'
}

export interface UserGroupsResponse {
  userGroups: UserGroup[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

class UserGroupService {
  private getAuthHeader() {
    const token = localStorage.getItem('authToken')
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  }

  /**
   * ユーザーグループ一覧を取得
   */
  async getUserGroups(options?: {
    page?: number
    limit?: number
    search?: string
    department?: string
  }): Promise<UserGroupsResponse> {
    const params = new URLSearchParams()

    if (options?.page) params.append('page', options.page.toString())
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.search) params.append('search', options.search)
    if (options?.department) params.append('department', options.department)

    const response = await fetch(`${API_BASE_URL}/user-groups?${params}`, {
      headers: this.getAuthHeader(),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'ユーザーグループの取得に失敗しました')
    }

    return response.json()
  }

  /**
   * ユーザーグループ詳細を取得
   */
  async getUserGroupById(id: string): Promise<{ userGroup: UserGroup }> {
    const response = await fetch(`${API_BASE_URL}/user-groups/${id}`, {
      headers: this.getAuthHeader(),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'ユーザーグループの取得に失敗しました')
    }

    return response.json()
  }

  /**
   * 自分が所属するグループ一覧を取得
   */
  async getMyUserGroups(): Promise<{ userGroups: UserGroup[] }> {
    const response = await fetch(`${API_BASE_URL}/user-groups/my`, {
      headers: this.getAuthHeader(),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'グループ一覧の取得に失敗しました')
    }

    return response.json()
  }

  /**
   * ユーザーグループを作成
   */
  async createUserGroup(
    data: CreateUserGroupRequest
  ): Promise<{ message: string; userGroup: UserGroup }> {
    const response = await fetch(`${API_BASE_URL}/user-groups`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'ユーザーグループの作成に失敗しました')
    }

    return response.json()
  }

  /**
   * ユーザーグループを更新
   */
  async updateUserGroup(
    id: string,
    data: UpdateUserGroupRequest
  ): Promise<{ message: string; userGroup: UserGroup }> {
    const response = await fetch(`${API_BASE_URL}/user-groups/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeader(),
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'ユーザーグループの更新に失敗しました')
    }

    return response.json()
  }

  /**
   * ユーザーグループを削除
   */
  async deleteUserGroup(id: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/user-groups/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeader(),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'ユーザーグループの削除に失敗しました')
    }

    return response.json()
  }

  /**
   * グループにメンバーを追加
   */
  async addMemberToGroup(
    groupId: string,
    data: AddMemberRequest
  ): Promise<{ message: string; member: UserGroupMember }> {
    const response = await fetch(`${API_BASE_URL}/user-groups/${groupId}/members`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'メンバーの追加に失敗しました')
    }

    return response.json()
  }

  /**
   * グループからメンバーを削除
   */
  async removeMemberFromGroup(groupId: string, memberId: string): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/user-groups/${groupId}/members/${memberId}`, {
      method: 'DELETE',
      headers: this.getAuthHeader(),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'メンバーの削除に失敗しました')
    }

    return response.json()
  }

  /**
   * メンバーの権限を更新
   */
  async updateMemberRole(
    groupId: string,
    memberId: string,
    data: UpdateMemberRoleRequest
  ): Promise<{ message: string; member: UserGroupMember }> {
    const response = await fetch(
      `${API_BASE_URL}/user-groups/${groupId}/members/${memberId}/role`,
      {
        method: 'PUT',
        headers: this.getAuthHeader(),
        body: JSON.stringify(data),
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'メンバー権限の更新に失敗しました')
    }

    return response.json()
  }
}

export const userGroupService = new UserGroupService()
